import { useState } from "react";
import { Mail, MapPin, MessageSquare, Send, Check } from "lucide-react";
import { toast } from "sonner";
import AuraGlow from "@/components/AuraGlow";
import FadeIn from "@/components/FadeIn";

const businessTypes = [
  "Cosmetics / Beauty",
  "Tradesperson",
  "Mechanic / Auto",
  "Salon / Spa",
  "Gym / Fitness",
  "Restaurant / Café",
  "Professional services",
  "E-commerce / Retail",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_type: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.business_type) e.business_type = "Pick a business type";
    if (form.message.trim().length < 5) e.message = "Tell us a little more (5+ chars)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
const onSubmit = async (ev) => {
  ev.preventDefault();

  if (!validate()) return;

  setSubmitting(true);

  try {
    const response = await fetch("https://formspree.io/f/mgodlowk", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(form),
    });

    if (response.ok) {
      setSuccess(true);
      toast.success("Thanks — we'll be in touch within 1 business day.");
      setForm({
        name: "",
        email: "",
        business_type: "",
        message: "",
      });
    } else {
      toast.error("Something went wrong. Please try again.");
    }
  } catch (err) {
    toast.error("Submission failed.");
  } finally {
    setSubmitting(false);
  }
};
  
  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 md:pt-40 pb-12" data-testid="contact-hero">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <AuraGlow className="right-[15%] top-[30%] hidden md:block" size={300} blur={80} opacity={0.12} />
        <div className="container-7 relative">
          <FadeIn>
            <p className="overline">Contact</p>
          </FadeIn>
          <FadeIn delay={120}>
            <h1 className="mt-4 max-w-4xl text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.05]">
              Let's <span className="gradient-text">build your website.</span>
            </h1>
          </FadeIn>
          <FadeIn delay={220}>
            <p className="mt-6 max-w-2xl text-lg text-[#94A3B8] leading-relaxed">
              Tell us about your business and what you'd like to achieve online.
              We'll reply within 1 business day with next steps and a clear quote.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="pb-24" data-testid="contact-section">
        <div className="container-7 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* INFO */}
          <aside className="lg:col-span-5">
            <FadeIn>
              <div className="surface-card p-8 relative overflow-hidden">
                <AuraGlow className="-right-10 -top-10" size={260} blur={120} opacity={0.18} />
                <p className="overline relative">Get in touch</p>
                <h2 className="mt-3 text-2xl font-medium relative">
                  Talk to a real human about your project.
                </h2>
                <ul className="mt-8 space-y-5 relative">
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1A2235] border border-[#1E293B] text-[#4DA3FF]">
                      <Mail size={16} />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Email</p>
                      <a
                        href="mailto:hello@webaurasites.co.uk"
                        data-testid="contact-email-link"
                        className="mt-1 inline-block text-white hover:text-[#4DA3FF] transition-colors"
                      >
                        hello@webaurasites.co.uk
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1A2235] border border-[#1E293B] text-[#4DA3FF]">
                      <MapPin size={16} />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Location</p>
                      <p className="mt-1 text-white">United Kingdom · Remote-first</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1A2235] border border-[#1E293B] text-[#4DA3FF]">
                      <MessageSquare size={16} />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Response time</p>
                      <p className="mt-1 text-white">Within 1 business day</p>
                    </div>
                  </li>
                </ul>

                <div className="mt-10 pt-6 border-t border-[#1E293B] text-sm text-[#94A3B8] relative">
                  <p>
                    Prefer email? Drop us a line at{" "}
                    <a
                      href="mailto:hello@webaurasites.co.uk"
                      className="text-[#4DA3FF] hover:underline"
                    >
                      hello@webaurasites.co.uk
                    </a>
                    .
                  </p>
                </div>
              </div>
            </FadeIn>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-7">
            <FadeIn delay={120}>
              {success ? (
                <div
                  className="surface-card p-10 text-center"
                  data-testid="contact-success-state"
                >
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#4DA3FF]/15 border border-[#4DA3FF]/40 text-[#4DA3FF]">
                    <Check size={22} strokeWidth={2} />
                  </div>
                  <h2 className="mt-6 text-2xl font-medium">Thanks — we got it.</h2>
                  <p className="mt-3 text-[#94A3B8]">
                    Your enquiry has been received. We'll be in touch within 1 business day
                    with a quick reply and the next steps.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="btn-secondary mt-8"
                    data-testid="contact-send-another"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  className="surface-card p-8 md:p-10"
                  data-testid="contact-form"
                  noValidate
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field
                      label="Your name"
                      id="name"
                      placeholder="Jane Doe"
                      value={form.name}
                      onChange={update("name")}
                      error={errors.name}
                      testId="contact-input-name"
                    />
                    <Field
                      label="Email"
                      id="email"
                      type="email"
                      placeholder="you@yourbusiness.co.uk"
                      value={form.email}
                      onChange={update("email")}
                      error={errors.email}
                      testId="contact-input-email"
                    />
                  </div>

                  <div className="mt-5">
                    <label
                      htmlFor="business_type"
                      className="block text-sm font-medium text-white mb-2"
                    >
                      Business type
                    </label>
                    <select
                      id="business_type"
                      value={form.business_type}
                      onChange={update("business_type")}
                      data-testid="contact-input-business-type"
                      className="w-full rounded-xl bg-[#1A2235] border border-[#1E293B] focus:border-[#4DA3FF] focus:ring-2 focus:ring-[#4DA3FF]/20 outline-none text-white px-4 py-3 transition"
                    >
                      <option value="" className="bg-[#0B0F1A]">
                        Select a business type
                      </option>
                      {businessTypes.map((b) => (
                        <option key={b} value={b} className="bg-[#0B0F1A]">
                          {b}
                        </option>
                      ))}
                    </select>
                    {errors.business_type && (
                      <p className="mt-2 text-xs text-red-400" data-testid="contact-error-business-type">
                        {errors.business_type}
                      </p>
                    )}
                  </div>

                  <div className="mt-5">
                    <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                      Tell us about your project
                    </label>
                    <textarea
                      id="message"
                      rows={6}
                      placeholder="A few lines about your business, what you need, timeline and budget..."
                      value={form.message}
                      onChange={update("message")}
                      data-testid="contact-input-message"
                      className="w-full rounded-xl bg-[#1A2235] border border-[#1E293B] focus:border-[#4DA3FF] focus:ring-2 focus:ring-[#4DA3FF]/20 outline-none text-white px-4 py-3 transition resize-none"
                    />
                    {errors.message && (
                      <p className="mt-2 text-xs text-red-400" data-testid="contact-error-message">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-xs text-[#64748B]">
                      We respect your privacy. Your message is only used to reply to your enquiry.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="contact-submit-button"
                    >
                      {submitting ? "Sending..." : "Send message"}
                      <Send size={15} />
                    </button>
                  </div>
                </form>
              )}
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, id, type = "text", placeholder, value, onChange, error, testId }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-white mb-2">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full rounded-xl bg-[#1A2235] border border-[#1E293B] focus:border-[#4DA3FF] focus:ring-2 focus:ring-[#4DA3FF]/20 outline-none text-white px-4 py-3 transition placeholder:text-[#64748B]"
      />
      {error && (
        <p className="mt-2 text-xs text-red-400" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
